import React, { useContext, useState, createContext, useEffect, ReactNode, useMemo } from 'react';
import { AlertColor } from '@mui/material';
import { useJsApiLoader } from '@react-google-maps/api';
import { routes } from '@/app/app';
import { useLocation } from 'react-router-dom';

export type SnackbarType = {
  message: string | ReactNode;
  open: boolean;
  severity: AlertColor;
};

type Link = {
  primary: string;
  link: string;
};

type AppContextType = {
  snackbar: SnackbarType;
  setSnackbar: React.Dispatch<React.SetStateAction<SnackbarType>>;
  selectedNav: Link;
  setSelectedNav: React.Dispatch<React.SetStateAction<Link>>;
  isGoogleMapsApiLoaded: boolean;
  googleMapsApiLoadError: Error | undefined;
  previewProfile: string | null;
  setPreviewProfile: React.Dispatch<React.SetStateAction<string | null>>;
  scrollPos: { [key: string]: number };
  setScrollPos: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>;
};

const AppContext = createContext<AppContextType>(null!);
export const useAppContext = () => useContext(AppContext);

// Pin to 3.64: DrawingManager was removed in Maps JS API 3.65.
const GOOGLE_MAPS_API_VERSION = '3.64';
const GOOGLE_MAPS_LIBRARIES: ('places' | 'drawing' | 'visualization')[] = ['places', 'drawing', 'visualization'];

export const AppContextProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const normalizedPath = useMemo(() => {
    const p = location.pathname.endsWith('/')
      ? location.pathname.slice(0, -1).toLowerCase()
      : location.pathname.toLowerCase();
    return p || '/';
  }, [location.pathname]);

  const [snackbar, setSnackbar] = useState<SnackbarType>({ message: '', open: false, severity: 'info' });
  const [selectedNav, setSelectedNav] = useState({ link: normalizedPath, primary: getPageName(normalizedPath) });
  const [previewProfile, setPreviewProfile] = useState<string | null>(null);
  const { isLoaded: isGoogleMapsApiLoaded, loadError: googleMapsApiLoadError } = useJsApiLoader({
    googleMapsApiKey: (import.meta as any).env.VITE_GOOGLE_MAPS_KEY,
    version: GOOGLE_MAPS_API_VERSION,
    language: 'iw',
    region: 'il',
    libraries: GOOGLE_MAPS_LIBRARIES
  });
  const [scrollPos, setScrollPos] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    setSelectedNav({ link: normalizedPath, primary: getPageName(normalizedPath) });
  }, [normalizedPath]);

  return (
    <AppContext.Provider
      value={{
        snackbar,
        setSnackbar,
        selectedNav,
        setSelectedNav,
        isGoogleMapsApiLoaded,
        googleMapsApiLoadError,
        previewProfile,
        setPreviewProfile,
        scrollPos,
        setScrollPos
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const getPageName = (pathname: string): string => {
  const flattenPagesList = routes.map(route => ({
    primary: route.primary,
    link: route.route.toLowerCase()
  }));
  // Remove trailing slash if present
  const normalizedPath = pathname.endsWith('/') ? pathname.slice(0, -1).toLowerCase() : pathname.toLowerCase();

  // Check for exact match first
  if (flattenPagesList.find(page => page.link === normalizedPath)) {
    return flattenPagesList.find(page => page.link === normalizedPath)?.primary || 'שלוחים-אפ';
  }

  // Check for partial matches for nested routes like '/crm/contact/:id'
  for (const route of flattenPagesList) {
    if (normalizedPath.startsWith(route.link) && route.link !== '/') {
      return route.primary || 'שלוחים-אפ';
    }
  }

  const segments = normalizedPath.split('/').filter(Boolean);
  for (let i = segments.length; i >= 1; i--) {
    const candidate = `/${segments.slice(0, i).join('/')}`;
    const byPrefix = flattenPagesList.find(page => page.link === candidate);
    if (byPrefix) {
      return byPrefix.primary || 'שלוחים-אפ';
    }
  }

  return 'שלוחים-אפ';
};
